<template>
  <v-dialog v-model="mainDialog" persistent max-width="400px">
    <v-card color="rgb(20,20,20)" dark class="pt-6">
      <v-card-text class="pb-0">
        <v-container class="ma-0 pa-0">
          <v-row no-gutters>
            <v-form ref="changeRoomForm" @submit.prevent style="width: 100%">
              <v-input class="changeInput">
                <v-col cols="12" class="ma-0 pa-0">
                  <v-text-field
                    aria-autocomplete="false"
                    v-model="roomNameInput"
                    solo
                    placeholder="Room name"
                    counter
                    maxlength="24"
                    validate-on-blur
                    :rules="roomNameRules"
                  ></v-text-field>
                  <v-textarea
                    aria-autocomplete="false"
                    v-model="roomDescriptionInput"
                    class="roomDescriptionInput mt-1 mb-0 pt-0 pb-0 whiteScroll"
                    :placeholder="
                      'Description' + (isPublic ? '' : ' (optional)')
                    "
                    solo
                    counter
                    no-resize
                    maxlength="128"
                    validate-on-blur
                    :rules="
                      isPublic
                        ? publicRoomDescriptionRules
                        : roomDescriptionRules
                    "
                  ></v-textarea>
                  <v-expansion-panels v-if="user.tier" class="mt-1">
                    <v-expansion-panel>
                      <v-expansion-panel-header class="orange--text">
                        Subscriber Options
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row no-gutters>
                          <v-col cols="6" class="ma-0 pa-0">
                            <v-select
                              v-model="categorySelector"
                              label="Category"
                              solo
                              :background-color="
                                enlistRoom || isPublic
                                  ? 'rgb(20,20,20)'
                                  : 'rgb(40,40,40)'
                              "
                              :disabled="!enlistRoom && !isPublic"
                              chips
                              :rules="categoryRules()"
                              :items="
                                categories.map(obj => {
                                  return obj.name;
                                })
                              "
                            >
                              <template v-slot:selection="{ item, index }">
                                <v-chip
                                  v-if="index < 2"
                                  :class="findColour(item, categories)"
                                  class="white--text"
                                >
                                  <span>{{ item }}</span>
                                </v-chip>
                                <span
                                  v-if="index == 2"
                                  class="grey--text caption"
                                >
                                  (+{{ categorySelector.length - 2 }} other{{
                                    categorySelector.length - 2 > 1 ? "s" : ""
                                  }})
                                </span>
                              </template>
                            </v-select>
                          </v-col>
                          <v-spacer></v-spacer>
                          <v-col cols="5" class="mb-0 pa-0">
                            <v-switch
                              v-model="isPublic"
                              color="black"
                              style="margin-top: -10px"
                              label="Public"
                              @change="enlistRoom = $event"
                            >
                              <v-tooltip slot="append" bottom>
                                <template v-slot:activator="{ on }">
                                  <v-icon v-on="on" style="pointer-events: all"
                                    >mdi-information</v-icon
                                  >
                                </template>
                                <span v-if="isPublic">
                                  Makes your room private (Only friends can
                                  join)
                                </span>
                                <span v-else>
                                  Makes your room public (Anyone can join)
                                </span>
                              </v-tooltip>
                            </v-switch>
                            <v-switch
                              v-model="enlistRoom"
                              color="black"
                              class="mt-2"
                              label="Listed"
                              :disabled="!isPublic"
                            >
                              <v-tooltip slot="append" bottom>
                                <template v-slot:activator="{ on }">
                                  <v-icon v-on="on" style="pointer-events: all"
                                    >mdi-information</v-icon
                                  >
                                </template>
                                <span v-if="enlistRoom">
                                  Removes your room from the public listings
                                </span>
                                <span v-else>
                                  Adds your room to the public listings
                                </span>
                              </v-tooltip>
                            </v-switch>
                          </v-col>
                          <v-col cols="12" class="ma-0 pa-0">
                            <v-text-field
                              v-model="coverURL"
                              solo
                              :background-color="
                                enlistRoom || isPublic
                                  ? 'rgb(20,20,20)'
                                  : 'rgb(40,40,40)'
                              "
                              :disabled="!enlistRoom && !isPublic"
                              placeholder="Cover image URL"
                              counter
                              maxlength="256"
                              aria-autocomplete="false"
                              :rules="coverURLRules()"
                              :error-messages="coverURLErrors"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>
              </v-input>
            </v-form>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="light-green accent-4" text @click="close()"> Close </v-btn>
        <v-btn color="light-green accent-4" text @click="attempt()"> Save </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style>
.roomDescriptionInput textarea {
  margin-top: 0px;
  margin-bottom: 0px;
  min-height: 64px;
  max-height: 96px;
}

.changeInput div.v-messages:last-child {
  display: none !important;
}
</style>

<script>
import { Utility } from "@/tools/utility.js";
import { Categories } from "@/tools/config.js";

export default {
  data: () => ({
    mainDialog: false,
    resolve: null,
    reject: null,
    roomNameRules: [
      v => !!v || "Room name is required",
      v => (v && v.length > 3) || "Must be > 3 characters",
      v => (v && v.length <= 25) || "Must be <= 25 characters"
    ],
    roomDescriptionRules: [
      v => !v || true,
      v => v.length == 0 || v.length > 15 || "Must be > 15 characters",
      v => v.length <= 128 || "Must be <= 128 characters"
    ],
    publicRoomDescriptionRules: [
      v => !!v || "Description is required",
      v => (v && v.length > 15) || "Must be > 15 characters",
      v => (v && v.length <= 128) || "Must be <= 128 characters"
    ],
    roomNameInput: "",
    roomDescriptionInput: "",
    roomID: "",
    coverURL: "",
    coverURLErrors: [],
    categorySelector: "",
    categories: Categories,
    enlistRoom: false,
    isPublic: false
  }),
  computed: {
    user() {
      return this.$store.state.user;
    }
  },
  watch: {
    coverURL: function(newV) {
      let $this = this;
      Utility.testImage(newV, function(outcome) {
        if (outcome == "success") {
          $this.coverURLErrors = [];
        } else {
          $this.coverURLErrors = ["URL isn't an image"];
        }
      });
    }
  },
  methods: {
    open(
      roomName,
      roomDescription,
      roomID,
      isPublic,
      roomCategory,
      roomCoverURL,
      isListed
    ) {
      this.roomNameInput = roomName;
      this.roomDescriptionInput = roomDescription;
      this.categorySelector = roomCategory || "";
      this.coverURL = roomCoverURL || "";
      this.enlistRoom = isListed;
      this.roomID = roomID;
      this.isPublic = isPublic;
      this.mainDialog = true;
      this.$nextTick(() => {
        if (this.$refs.changeRoomForm) {
          this.$refs.changeRoomForm.resetValidation();
        }
      });
    },
    close() {
      this.mainDialog = false;
      this.roomNameInput = "";
      this.roomDescriptionInput = "";
      this.roomID = "";
      this.$refs.changeRoomForm.resetValidation();
    },
    attempt() {
      let $this = this;
      if (this.$refs.changeRoomForm.validate()) {
        let object = {
          user: this.user,
          roomID: this.roomID,
          newName: this.roomNameInput,
          newDescription: this.roomDescriptionInput,
          newCoverURL: this.coverURL,
          newCategory: this.categorySelector,
          newIsPublic: this.isPublic,
          newIsListed: this.enlistRoom
        };
        this.$socket.emit("change room details", object, result => {
          if (result.response == "invalid input") {
            this.$root.$alert("Error", "Invalid input.", {
              color: "red accent-4"
            });
          } else if (result.response == "invalid tier") {
            this.$root.$alert(
              "Error",
              "Only Advocates can use GIFs as cover URLs!",
              {
                color: "red accent-4"
              }
            );
          } else if (result.response == "success") {
            $this.close();
          }
        });
      }
    },
    findColour(item, categories) {
      for (let i = 0; i < categories.length; i++) {
        if (categories[i].name == item) {
          return categories[i].colour;
        }
      }
      return "black";
    },
    categoryRules() {
      let v = this.categorySelector;
      if (v == null) return false;
      return [
        (!v.length && !this.enlistRoom && !this.isPublic) ||
          v.length ||
          "Category is required"
      ];
    },
    coverURLRules() {
      let v = this.coverURL;
      if (v == null) return false;
      return [
        (!v.length && !this.enlistRoom && !this.isPublic) ||
          v.length ||
          "Cover URL is required",

        new RegExp(
          "^(https?:\\/\\/)?" +
            "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" +
            "((\\d{1,3}\\.){3}\\d{1,3}))" +
            "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" +
            "(\\?[;&a-z\\d%_.~+=-]*)?" +
            "(\\#[-a-z\\d_]*)?$",
          "i"
        ).test(v) ||
          !v.length ||
          "URL is invalid"
      ];
    }
  }
};
</script>
